import React, { useState, useEffect } from 'react';
import { Search, Calendar, ShoppingBag, Compass, Menu, Sparkles } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';

const BottomNav = ({ activeTab, setActiveTab, scrollDirection = 'up', onSearch, stayOnPageAfterAuth = false }) => {
  const { userMode, cartCount, todayAgendaCount, requireAuth, setAiChatOpen, isLoggedIn } = useApp();
  const { t } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    setIsCollapsed(scrollDirection === 'down');
  }, [scrollDirection]);

  const consumerTabs = [
    { id: 'explore', icon: Compass, label: t('nav', 'explore') },
    { id: 'calendar', icon: Calendar, label: t('nav', 'calendar'), badge: todayAgendaCount > 0 ? todayAgendaCount : null },
    { id: 'orders', icon: ShoppingBag, label: t('nav', 'orders'), badge: cartCount > 0 ? cartCount : null },
    { id: 'profile', icon: Menu, label: t('nav', 'menu') }
  ];

  const partnerTabs = [
    { id: 'dashboard', icon: Compass, label: 'Dashboard' },
    { id: 'calendar', icon: Calendar, label: 'Agenda' },
    { id: 'orders', icon: ShoppingBag, label: 'Ordini' },
    { id: 'profile', icon: Menu, label: t('nav', 'menu') }
  ];

  const tabs = userMode === 'consumer' ? consumerTabs : partnerTabs;
  const leftTabs = tabs.slice(0, Math.ceil(tabs.length / 2));
  const rightTabs = tabs.slice(Math.ceil(tabs.length / 2));

  const handleTabClick = (tabId) => {
    const protectedTabs = ['calendar', 'orders', 'profile', 'dashboard', 'orders-received'];
    if (protectedTabs.includes(tabId)) {
      if (stayOnPageAfterAuth) {
        requireAuth(() => {
          // Do nothing - user stays on current page after login
        });
      } else {
        requireAuth(() => setActiveTab(tabId));
      }
    } else {
      setActiveTab(tabId);
    }
  };

  const handleSearchClick = () => {
    if (onSearch) {
      onSearch();
    } else {
      setActiveTab('search-results');
    }
  };

  const handleAIClick = () => {
    setAiChatOpen(true);
  };

  const TabButton = ({ tab }) => {
    const isActive = activeTab === tab.id;
    return (
      <button
        onClick={() => handleTabClick(tab.id)}
        className="relative flex flex-col items-center justify-center min-w-[64px] sm:min-w-[74px] md:min-w-[84px] py-2 group"
      >
        {/* Active indicator line */}
        <div className={`absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-full transition-all duration-300 ${
          isActive ? 'bg-emerald-500 scale-100' : 'scale-0'
        }`} />
        
        {/* Icon container */}
        <div className={`relative flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-2xl transition-all duration-300 ${
          isActive 
            ? 'bg-emerald-50' 
            : 'group-hover:bg-gray-50'
        }`}>
          <tab.icon 
            className={`w-[22px] h-[22px] sm:w-6 sm:h-6 transition-all duration-300 ${
              isActive 
                ? 'text-emerald-600' 
                : 'text-gray-400 group-hover:text-gray-600'
            }`}
            strokeWidth={isActive ? 2.5 : 2}
          />
          
          {/* Badge */}
          {tab.badge && (
            <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
              {tab.badge > 9 ? '9+' : tab.badge}
            </span>
          )}
        </div>
        
        {/* Label */}
        <span className={`mt-1 text-[11px] sm:text-xs font-medium transition-all duration-300 ${
          isActive ? 'text-emerald-600' : 'text-gray-400 group-hover:text-gray-600'
        }`}>
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 lg:hidden">
      {/* Collapsed state - different for consumer vs partner */}
      <div className={`absolute bottom-4 left-4 right-4 transition-all duration-300 ${
        isCollapsed ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        <div className="flex items-center gap-3 max-w-lg mx-auto justify-center">
          {/* Search bar only for consumer mode */}
          {userMode === 'consumer' && (
            <button 
              onClick={handleSearchClick}
              className="flex-1 flex items-center gap-3 bg-white/95 backdrop-blur-lg rounded-full px-5 py-4 shadow-lg border border-gray-100"
            >
              <Search className="w-5 h-5 text-emerald-500" />
              <span className="text-gray-400 text-sm">{t('common', 'search')}...</span>
            </button>
          )}
          <button
            onClick={handleAIClick}
            className="w-14 h-14 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/25"
          >
            <Sparkles className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Main tab bar */}
      <div className={`transition-all duration-300 ${
        isCollapsed ? 'translate-y-full' : 'translate-y-0'
      }`}>
        {/* Glass background */}
        <div className="absolute inset-0 bg-white/90 backdrop-blur-xl border-t border-gray-100" />
        
        {/* Content */}
        <div className="relative flex items-center justify-center h-[80px] sm:h-[84px] md:h-[88px] max-w-2xl mx-auto">
          {/* Left tabs */}
          <div className="flex items-center justify-center gap-1 sm:gap-3">
            {leftTabs.map((tab) => (
              <TabButton key={tab.id} tab={tab} />
            ))}
          </div>

          {/* Center AI Button */}
          <div className="relative mx-4 sm:mx-6 md:mx-8">
            <button
              onClick={handleAIClick}
              className="relative group"
            >
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-400 rounded-full blur-lg opacity-40 group-hover:opacity-60 transition-opacity scale-110" />
              
              {/* Button */}
              <div className="relative w-[56px] h-[56px] sm:w-[62px] sm:h-[62px] md:w-16 md:h-16 bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-500 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30 group-hover:scale-105 group-active:scale-95 transition-all duration-200">
                {/* Inner glow */}
                <div className="absolute inset-[3px] bg-gradient-to-br from-white/25 to-transparent rounded-full" />
                <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-white relative z-10" strokeWidth={2.5} />
              </div>
              
              {/* Notification dot */}
              <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white" />
            </button>
          </div>

          {/* Right tabs */}
          <div className="flex items-center justify-center gap-1 sm:gap-3">
            {rightTabs.map((tab) => (
              <TabButton key={tab.id} tab={tab} />
            ))}
          </div>
        </div>
        
        {/* Safe area spacer */}
        <div className="h-safe-area-bottom bg-white/90 backdrop-blur-xl" />
      </div>
    </nav>
  );
};

export default BottomNav;
